<template>
  <v-dialog v-model="show" persistent max-width="800px">
    <v-card class="mb-0">
      <v-toolbar dark color="primary">
        <v-btn icon dark @click="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>Nuevo Producto</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="mt-8">
        <v-form ref="form" v-model="valid" :lazy-validation="lazy">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="producto.codigo"
                prepend-icon="mdi-barcode"
                label="Código/SKU *"
                :rules="requiredRules"
                dense
                maxlength="50"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="producto.nombre"
                prepend-icon="mdi-package-variant"
                label="Nombre *"
                :rules="requiredRules"
                dense
                maxlength="100"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12">
              <v-textarea
                v-model="producto.descripcion"
                prepend-icon="mdi-text"
                label="Descripción"
                rows="2"
                dense
                maxlength="500"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select
                v-model="producto.idCategoria"
                :items="categorias"
                item-text="nombre"
                item-value="idCategoria"
                label="Categoría *"
                :rules="requiredRules"
                dense
                prepend-icon="mdi-tag"
              >
                <template v-slot:append-outer>
                  <v-btn icon small @click="abrirNuevaCategoria">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="producto.unidadMedida"
                :items="unidadesMedida"
                label="Unidad de Medida *"
                dense
                prepend-icon="mdi-ruler"
              ></v-select>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="producto.precioCosto"
                prepend-icon="mdi-currency-usd"
                label="Precio de Costo *"
                :rules="precioRules"
                @keypress="onlyNumber"
                @blur="toFixedCosto"
                dense
                maxlength="10"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="producto.precioVenta"
                prepend-icon="mdi-currency-usd"
                label="Precio de Venta *"
                :rules="precioRules"
                @keypress="onlyNumber"
                @blur="toFixedVenta"
                dense
                maxlength="10"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="producto.stockInicial"
                prepend-icon="mdi-package"
                label="Stock Inicial *"
                :rules="stockRules"
                @keypress="onlyNumber"
                dense
                maxlength="10"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="producto.stockMinimo"
                prepend-icon="mdi-alert"
                label="Stock Mínimo *"
                :rules="stockRules"
                @keypress="onlyNumber"
                dense
                maxlength="10"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="producto.puntoReposicion"
                prepend-icon="mdi-reload"
                label="Punto de Reposición *"
                :rules="stockRules"
                @keypress="onlyNumber"
                dense
                maxlength="10"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="producto.fechaVencimiento"
                prepend-icon="mdi-calendar"
                label="Fecha de Vencimiento"
                type="date"
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="producto.estado"
                :items="estados"
                label="Estado *"
                :rules="requiredRules"
                dense
                prepend-icon="mdi-check-circle"
              ></v-select>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text color="grey lighten-1" @click.stop="show = false">
          Cancelar
        </v-btn>
        <v-btn color="success" :disabled="!valid" @click.stop="guardarProducto">
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "@/axios-auth";
import { showAlert } from "@/utils/toast";

export default {
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      valid: false,
      lazy: false,
      producto: {
        codigo: "",
        nombre: "",
        descripcion: "",
        idCategoria: null,
        precioCosto: null,
        precioVenta: null,
        stockInicial: 0,
        stockMinimo: 0,
        puntoReposicion: 0,
        unidadMedida: "unidad",
        fechaVencimiento: null,
        estado: "activo",
      },
      categorias: [],
      unidadesMedida: [
        { text: "Unidad", value: "unidad" },
        { text: "Kilogramo", value: "kg" },
        { text: "Gramo", value: "g" },
        { text: "Litro", value: "litro" },
        { text: "Mililitro", value: "ml" },
        { text: "Metro", value: "metro" },
        { text: "Caja", value: "caja" },
        { text: "Pack", value: "pack" },
      ],
      estados: [
        { text: "Activo", value: "activo" },
        { text: "Inactivo", value: "inactivo" },
      ],
      requiredRules: [(v) => !!v || "Dato obligatorio"],
      precioRules: [
        (v) => !!v || "Dato obligatorio",
        (v) => parseFloat(v) >= 0 || "El precio debe ser mayor o igual a 0",
      ],
      stockRules: [
        (v) => (v !== null && v !== "") || "Dato obligatorio",
        (v) => parseFloat(v) >= 0 || "El stock debe ser mayor o igual a 0",
      ],
    };
  },
  computed: {
    show: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.vaciarModels();
          this.$emit("close");
        }
      },
    },
  },
  methods: {
    cargarCategorias() {
      let self = this;
      axios
        .get("/Producto/GetCategorias", {
          params: {
            idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
          },
        })
        .then(function (response) {
          if (response.data) {
            self.categorias = response.data;
            self.producto.idCategoria = self.categorias[0].idCategoria;
          }
        })
        .catch(function (error) {
          self.categorias = [];
        });
    },

    abrirNuevaCategoria() {
      // TODO: Implementar modal de nueva categoría
      this.showAlert({
        icon: "info",
        title: "Funcionalidad en desarrollo",
        vm: this,
        timer: 2000,
      });
    },

    toFixedCosto() {
      if (isNaN(parseFloat(this.producto.precioCosto)))
        this.producto.precioCosto = parseFloat(0.0).toFixed(2);
      else
        this.producto.precioCosto = parseFloat(
          this.producto.precioCosto,
        ).toFixed(2);
    },

    toFixedVenta() {
      if (isNaN(parseFloat(this.producto.precioVenta)))
        this.producto.precioVenta = parseFloat(0.0).toFixed(2);
      else
        this.producto.precioVenta = parseFloat(
          this.producto.precioVenta,
        ).toFixed(2);
    },

    onlyNumber($event) {
      let keyCode = $event.keyCode ? $event.keyCode : $event.which;
      if ((keyCode < 48 || keyCode > 57) && keyCode != 46) {
        $event.preventDefault();
      }
    },

    vaciarModels() {
      this.producto = {
        codigo: "",
        nombre: "",
        descripcion: "",
        idCategoria: null,
        precioCosto: null,
        precioVenta: null,
        stockInicial: 0,
        stockMinimo: 0,
        puntoReposicion: 0,
        unidadMedida: "unidad",
        fechaVencimiento: null,
        estado: "activo",
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    guardarProducto() {
      this.$swal({
        title: "Guardar Producto",
        text: "¿Desea registrar este producto?",
        icon: "success",
        type: "success",
        showCancelButton: true,
        background: "#ececec",
        confirmButtonText: "Si",
        cancelButtonText: "No",
        showCloseButton: true,
        showLoaderOnConfirm: true,
      }).then((result) => {
        if (result.value) {
          const datos = {
            ...this.producto,
            usuario: this.$store.state.usuario.usuario,
            idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
          };

          let self = this;
          axios
            .post("/Producto/GuardarProducto", datos)
            .then((response) => {
              if (response.data == "error") {
                self.showAlert({
                  icon: "warning",
                  title:
                    "Ya existe un producto registrado con ese nombre o ese código.",
                  vm: self,
                  timer: 2000,
                });
              } else {
                self.showAlert({
                  icon: "success",
                  title: "Producto registrado exitosamente.",
                  vm: self,
                  timer: 2000,
                });
                self.vaciarModels();
                self.$emit("guardado");
                self.show = false;
              }
            })
            .catch((error) => {
              self.showAlert({
                icon: "warning",
                title:
                  error.response?.data || "No se pudo registrar el producto.",
                vm: self,
                timer: 2000,
              });
            });
        }
      });
    },

    showAlert(alertInfo) {
      showAlert(alertInfo);
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.cargarCategorias();
      }
    },
  },
};
</script>
